import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Image, Edit3, Trash2, X } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import { sliderService } from '../../services/api'

const SliderTab = () => {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingSlide, setEditingSlide] = useState(null)
  const [slideForm, setSlideForm] = useState({
    title: '',
    description: '',
    image: null,
    link_url: '',
    sort_order: 0,
    is_active: true
  })
  const [imagePreview, setImagePreview] = useState(null)

  // Fetch slides using React Query
  const { data: slides = [], isLoading: slidesLoading } = useQuery({
    queryKey: ['admin-slides'],
    queryFn: () => sliderService.getSlidesAdmin()
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => sliderService.deleteSlide(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-slides'])
    }
  })

  const createSlideMutation = useMutation({
    mutationFn: (slideData) => sliderService.createSlide(slideData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-slides'])
      setShowModal(false)
      resetForm()
    }
  })

  const updateSlideMutation = useMutation({
    mutationFn: ({ id, slideData }) => sliderService.updateSlide(id, slideData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-slides'])
      setShowModal(false)
      resetForm()
    }
  })

  const resetForm = () => {
    setSlideForm({
      title: '',
      description: '',
      image: null,
      link_url: '',
      sort_order: 0,
      is_active: true
    })
    setEditingSlide(null)
    setImagePreview(null)
  }

  const handleAddSlide = () => {
    resetForm()
    setShowModal(true)
  }

  const handleEditSlide = (slide) => {
    setEditingSlide(slide)
    setSlideForm({
      title: slide.title || '',
      description: slide.description || '',
      image: null,
      link_url: slide.link_url || '',
      sort_order: slide.sort_order || 0,
      is_active: slide.is_active || true
    })
    if (slide.image) {
      setImagePreview(`/uploads/slider/${slide.image}`)
    }
    setShowModal(true)
  }

  const handleDeleteSlide = (slideId) => {
    if (window.confirm('هل أنت متأكد من أنك تريد حذف هذه الشريحة؟')) {
      deleteMutation.mutate(slideId)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const formData = new FormData()
    formData.append('title', slideForm.title)
    formData.append('description', slideForm.description)
    formData.append('link_url', slideForm.link_url)
    formData.append('sort_order', slideForm.sort_order)
    formData.append('is_active', slideForm.is_active)
    
    if (slideForm.image) {
      formData.append('image', slideForm.image)
    }

    if (editingSlide) {
      updateSlideMutation.mutate({ id: editingSlide.id, slideData: formData })
    } else {
      createSlideMutation.mutate(formData)
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSlideForm({ ...slideForm, image: file })
      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">إدارة الشرائح</h2>
        <button 
          onClick={handleAddSlide}
          className="flex items-center px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800"
        >
          <Plus className="h-4 w-4 mr-2" />
          إضافة شريحة
        </button>
      </div>
      
      <div className="bg-white shadow rounded-lg border">
        <div className="px-6 py-4">
          {slidesLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : slides && slides.length > 0 ? (
            <div className="space-y-4">
              {slides.map((slide) => (
                <div key={slide.id} className="border rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                  <div className="flex items-center space-x-4">
                    <img
                      src={`/uploads/slider/${slide.image}`}
                      alt={slide.title}
                      className="w-16 h-16 object-cover rounded flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-gray-900 truncate">{slide.title || 'Untitled'}</h4>
                      <p className="text-sm text-gray-500 truncate">{slide.description}</p>
                      {slide.link_url && (
                        <a href={slide.link_url} className="text-sm text-blue-600 hover:underline truncate block" target="_blank" rel="noopener noreferrer">
                          {slide.link_url}
                        </a>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          slide.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {slide.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-gray-500">Order: {slide.sort_order}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end space-x-2 md:flex-shrink-0">
                    <button 
                      onClick={() => handleEditSlide(slide)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 flex items-center justify-center"
                      title="Edit Slide"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteSlide(slide.id)}
                      className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 flex items-center justify-center"
                      title="Delete Slide"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Image className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Slides Yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                إنشاء شرائح جميلة للصفحة الرئيسية. أضف صور مع عناوين ووصوف وروابط.
              </p>
              <button 
                onClick={handleAddSlide}
                className="mt-4 flex items-center px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800"
              >
                <Plus className="h-4 w-4 mr-2" />
                إنشاء شريحتك الأولى
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Slide Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingSlide ? 'تعديل الشريحة' : 'إضافة شريحة جديدة'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  العنوان
                </label>
                <input
                  type="text"
                  value={slideForm.title}
                  onChange={(e) => setSlideForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الوصف
                </label>
                <textarea
                  value={slideForm.description}
                  onChange={(e) => setSlideForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رابط الشريحة
                </label>
                <input
                  type="url"
                  value={slideForm.link_url}
                  onChange={(e) => setSlideForm(prev => ({ ...prev, link_url: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ترتيب الشريحة
                </label>
                <input
                  type="number"
                  value={slideForm.sort_order}
                  onChange={(e) => setSlideForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الصورة
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="mt-2 w-32 h-20 object-cover rounded"
                  />
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={slideForm.is_active}
                  onChange={(e) => setSlideForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  شريحة نشطة
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={createSlideMutation.isLoading || updateSlideMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {(createSlideMutation.isLoading || updateSlideMutation.isLoading) ? 'جاري الحفظ...' : 'حفظ'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default SliderTab
